// Разговор с AI по уроку (после урока — «Закрепить в разговоре с AI»):
// задача «используйте в ответах 3 слова урока». Общая логика для сервера
// (выбор слов при создании сессии) и клиента (подсветка употреблённых слов).
// Засчитывает задачу и начисляет бонус — БД (complete_chat_task в миграции
// 20260911180000_lesson_chat.sql); сравнение там устроено так же, как здесь.

export const LESSON_CHAT_TASK_SIZE = 3
export const LESSON_CHAT_BONUS_XP = 5

// Свёртка для сравнения: регистр и турецкая/циркумфлексная диакритика не важны.
// Тот же набор букв, что public.tr_fold в БД.
export function trFold(text: string): string {
  const map: Record<string, string> = {
    İ: 'i', I: 'i', ı: 'i', Ş: 's', ş: 's', Ğ: 'g', ğ: 'g', Ü: 'u', ü: 'u',
    Ö: 'o', ö: 'o', Ç: 'c', ç: 'c', Â: 'a', â: 'a', Î: 'i', î: 'i', Û: 'u', û: 'u',
  }
  return text.replace(/[İIıŞşĞğÜüÖöÇçÂâÎîÛû]/g, (ch) => map[ch]).toLowerCase()
}

// Основа слова: без последней буквы, но не короче 3 — чтобы окончания и
// чередование на стыке (kitap → kitabı, zeytin → zeytini) не мешали.
export function taskStem(word: string): string {
  const folded = trFold(word.trim())
  return folded.slice(0, Math.max(3, folded.length - 1))
}

// Годится ли слово урока в задачу: одно слово (не фраза), без служебных
// «слов» алфавита, не короче 3 букв — короткие («o», «ve») находились бы
// внутри чего угодно.
function isTaskCandidate(turkish: string, grammarTag: string | null | undefined): boolean {
  if (grammarTag === 'alphabet') return false
  const t = turkish.trim()
  return t.length >= 3 && !/\s/.test(t) && /^[\p{L}-]+$/u.test(t)
}

// Все слова урока, годные в задачу, без повторов.
export function taskWordCandidates(vocabulary: { turkish: string; grammarTag?: string | null }[]): string[] {
  const seen = new Set<string>()
  return vocabulary
    .filter((v) => isTaskCandidate(v.turkish, v.grammarTag))
    .map((v) => v.turkish.trim())
    .filter((w) => {
      const key = trFold(w)
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
}

// 3 случайных слова урока (random — на каждый новый разговор свои).
export function pickTaskWords(
  vocabulary: { turkish: string; grammarTag?: string | null }[],
  random: () => number = Math.random,
): string[] {
  const candidates = taskWordCandidates(vocabulary)
  for (let i = candidates.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1))
    ;[candidates[i], candidates[j]] = [candidates[j], candidates[i]]
  }
  return candidates.slice(0, LESSON_CHAT_TASK_SIZE)
}

// Хвост после основы: чередование последней буквы (kitap -> kitabı) плюс
// окончания, всего не больше TASK_TAIL_MAX букв. Раньше искали подстроку
// основы где угодно, и "bir" засчитывался внутри "birlikte", а "ad" внутри
// "adres" (08.10).
export const TASK_TAIL_MAX = 4

function escapeRe(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\-]/g, '\\$&')
}

// Есть ли в тексте слово задачи как отдельное слово: основа на границе слова
// и хвост из букв до следующей границы не длиннее TASK_TAIL_MAX (плюс одна
// буква на место отброшенной основой последней: kitap -> kita + b + ı).
export function hasTaskWord(foldedText: string, word: string): boolean {
  const stem = taskStem(word)
  const dropped = trFold(word.trim()).length > stem.length ? 1 : 0
  const re = new RegExp(`(?<![\\p{L}])${escapeRe(stem)}[\\p{L}]{0,${TASK_TAIL_MAX + dropped}}(?![\\p{L}])`, 'u')
  return re.test(foldedText)
}

// Какие слова задачи уже встретились в репликах ученика.
export function usedTaskWords(taskWords: string[], userMessages: string[]): Set<string> {
  const folded = userMessages.map(trFold)
  const used = new Set<string>()
  for (const word of taskWords) {
    if (folded.some((m) => hasTaskWord(m, word))) used.add(word)
  }
  return used
}

// ── Разговор как сценарий урока (.scratch/engagement, этап 2.3) ──
// AI играет A из короткого диалога урока, ученик — B. Слова задачи —
// из реплик B (в порядке появления), детерминированно: одна первая реплика
// AI на урок в кэше (lessonOpeningBase), прогрев уроков работает как раньше.
type ScenarioLine = { speaker: string; tr: string; ru: string }

export function scenarioLines(dialogue: { short?: ScenarioLine[] } | null | undefined): ScenarioLine[] {
  return (dialogue?.short ?? []).slice(0, 8)
}

// Текст миссии для баннера: по русским переводам реплик B.
export function scenarioMission(dialogue: { short?: ScenarioLine[] } | null | undefined): string | null {
  const b = scenarioLines(dialogue).filter((l) => l.speaker === 'B').map((l) => l.ru.trim()).filter(Boolean)
  if (b.length === 0) return null
  const quoted = b.slice(0, 2).map((q) => `"${q.replace(/[.!]+$/, '')}"`).join(', ')
  return `Вы - B. Ответьте по смыслу, как в диалоге: ${quoted}${b.length > 2 ? ' и дальше по ситуации' : ''}.`
}

export function pickScenarioTaskWords(
  vocabulary: { turkish: string; grammarTag?: string | null }[],
  dialogue: { short?: ScenarioLine[] } | null | undefined,
): string[] {
  const candidates = taskWordCandidates(vocabulary)
  const bText = scenarioLines(dialogue)
    .filter((l) => l.speaker === 'B')
    .map((l) => trFold(l.tr))
    .join(' ')
  const inB = candidates
    .map((w) => ({ w, at: bText.indexOf(taskStem(w)) }))
    .filter((x) => x.at >= 0)
    .sort((a, b) => a.at - b.at)
    .map((x) => x.w)
  const rest = candidates.filter((w) => !inB.includes(w))
  return [...inB, ...rest].slice(0, LESSON_CHAT_TASK_SIZE)
}
